import { isArray } from '@apollo/client/cache/inmemory/helpers';
import i18next from 'i18next';
import moment from 'moment';

import { PersonFilter, PersonFilterInput } from '~/types/api.graphql';
import { lowercaseFirstLetter, omitTypename } from '~/utils/helpers';
import {
  BOOLEAN_OPERATOR_KEYS,
  BooleanOperatorKeys,
  DateOperatorKeys,
  EXTENDED_STRING_OPERATOR_KEYS,
  ExtendedStringFieldFilterHelper,
  ExtendedStringOperatorKeys,
  NUMBER_OPERATOR_KEYS,
  NumberOperatorKeys,
  NumberOperatorOperatorKey,
  STRING_OPERATOR_KEYS,
  StringOperatorKeys,
  StringOperatorOperatorKey,
} from '~/views/audiences/NewGlobalSearchAudiencePage/helpers/FilterHelper';

type Nullable<T> = T | null;

export enum PersonFilterType {
  String,
  ExtendedString,
  Number,
  Boolean,
  Geo,
  Date,
}
type ConditionType1 = NumberOperatorKeys | StringOperatorKeys;
type ConditionType2 = ExtendedStringOperatorKeys | BooleanOperatorKeys | DateOperatorKeys;
export type ConditionType = Nullable<ConditionType1 | ConditionType2>;

type StringFilterValue = string | string[] | number | boolean;
type ExtendedStringFilterValue = string | string[] | boolean;
type NumberFilterValue = number | number[] | object;
type BooleanFilterValue = boolean;
type GeoFilterValue = null;
type DateFilterValue = string;

abstract class PersonFilterHelper<ValueType, ConditionType> {
  protected filter: PersonFilter;

  constructor(filter: PersonFilter) {
    this.filter = filter;
  }

  public static createInstance(filter: PersonFilter): PersonFilterHelper<any, any> {
    if (!filter) return undefined;
    if (filter.stringFilter) return new StringFilterHelper(filter);
    if (filter.extendedStringFilter) return new ExtendedStringFilterHelper(filter);
    if (filter.numberFilter) return new NumberFilterHelper(filter);
    if (filter.booleanFilter) return new BooleanFilterHelper(filter);
    if (filter.geoFilter) return new GeoFilterHelper(filter);
    if (filter.dateFilter) return new DateFilterHelper(filter);

    return undefined;
  }

  getFilter(): PersonFilter {
    return this.filter;
  }

  isValidFilter(): boolean {
    return this.getCondition() === 'exists' || (!!this.getCondition() && !!this.getValue());
  }

  protected getFieldTitle(field: string) {
    const filterKey = `audience.filters.${lowercaseFirstLetter(field)}`;
    return i18next.exists(filterKey)
      ? i18next.t(filterKey)
      : i18next.t(`audience.people.${lowercaseFirstLetter(field)}`);
  }

  abstract getType(): PersonFilterType;
  abstract getField(): string;
  abstract getTitle(): string;
  abstract getTooltip(separatorText?: string): string;
  abstract getFilterInput(): PersonFilterInput;
  abstract getCondition(): ConditionType;
  abstract getValue(): ValueType;
  abstract getValueText(): string;
  abstract shouldShowPopover(): boolean;
  abstract isCustom(): boolean;

  isValueInputDisabled(): boolean {
    return !(this.getField() && this.getCondition());
  }

  isValueInputHidden(): boolean {
    return this.getCondition() === 'exists';
  }

  isMultiselect(): boolean {
    return false;
  }

  isNegation(): boolean {
    return false;
  }

  shouldShowCustomInput(): boolean {
    return false;
  }
}

class StringFilterHelper extends PersonFilterHelper<StringFilterValue, StringOperatorKeys> {
  getType(): PersonFilterType {
    return PersonFilterType.String;
  }

  getField(): string {
    return this.filter.stringFilter.customField || this.filter.stringFilter.field;
  }

  getTitle(): string {
    const field = this.getField();
    return this.filter.stringFilter.customField ? field : this.getFieldTitle(field);
  }

  getTooltip(separatorText?: string): string {
    const separatorKey = separatorText?.toLowerCase() || 'or';
    const separator = ` ${i18next.t(`audience.filters.${separatorKey}`)} `;

    let value;
    if (this.filter.stringFilter.operator?.in) {
      const values = this.filter.stringFilter.operator.in;
      const MAX_VALUES_TO_SHOW = 20;

      if (values.length > MAX_VALUES_TO_SHOW) {
        const visibleValues = values.slice(0, MAX_VALUES_TO_SHOW);
        const remainingCount = values.length - MAX_VALUES_TO_SHOW;
        value =
          visibleValues.join(separator) +
          ` ${separator}... ${i18next.t('audience.filters.more', { count: remainingCount })}`;
      } else {
        value = values.join(separator);
      }
    } else if (typeof this.filter.stringFilter.operator?.exists === 'boolean') {
      if (this.filter.stringFilter.operator?.exists) {
        return i18next.t('audience.filters.stringFilterExistsTooltip', {
          name: this.getTitle(),
          value: this.filter.stringFilter.operator.exists,
          interpolation: { escapeValue: false },
        });
      } else {
        return i18next.t('audience.filters.stringFilterNotExistsTooltip', {
          name: this.getTitle(),
          value: this.filter.stringFilter.operator.exists,
        });
      }
    } else {
      value = this.filter.stringFilter.operator?.eq;
    }

    const isNegate = this.isNegation();
    return i18next.t(`audience.filters.stringFilterTooltip${isNegate ? 'Negate' : ''}`, {
      name: this.getTitle(),
      value,
      interpolation: { escapeValue: false },
    });
  }

  getFilterInput(): PersonFilterInput {
    const fieldConfig = this.filter.stringFilter.customField
      ? { customField: this.filter.stringFilter.customField }
      : { field: this.filter.stringFilter.field };

    return {
      stringFilter: {
        operator: {
          ...(this.filter.stringFilter.operator.eq && { eq: this.filter.stringFilter.operator.eq }),
          ...(this.filter.stringFilter.operator.in && { in: this.filter.stringFilter.operator.in }),
          ...(typeof this.filter.stringFilter.operator.exists === 'boolean' && {
            exists: this.filter.stringFilter.operator.exists,
          }),
        },
        negate: this.filter.stringFilter.negate,
        ...fieldConfig,
      },
    };
  }

  getCondition(): StringOperatorKeys {
    if (!this.filter.stringFilter.operator) return null;
    return STRING_OPERATOR_KEYS.find((key) => key === Object.keys(this.filter.stringFilter.operator)[0]);
  }

  getValue(): StringFilterValue {
    if (!this.filter.stringFilter.operator) return null;
    if (this.filter.stringFilter.operator?.eq) {
      return isArray(this.filter.stringFilter.operator?.eq)
        ? this.filter.stringFilter.operator?.eq?.[0]
        : this.filter.stringFilter.operator?.eq;
    }
    return this.filter.stringFilter.operator?.in;
  }

  getValueText(): string {
    const value = this.getValue();
    if (!value) return '';
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value?.toString();
  }

  shouldShowPopover(): boolean {
    return true;
  }

  isMultiselect(): boolean {
    return this.getCondition() === StringOperatorOperatorKey.in;
  }

  isNegation(): boolean {
    if (typeof this.filter.stringFilter?.operator?.exists === 'boolean')
      return !this.filter.stringFilter.operator.exists;
    return this.filter.stringFilter.negate;
  }

  isCustom(): boolean {
    return !!this.filter.stringFilter.customField;
  }
}

class ExtendedStringFilterHelper extends PersonFilterHelper<ExtendedStringFilterValue, ExtendedStringOperatorKeys> {
  getType(): PersonFilterType {
    return PersonFilterType.ExtendedString;
  }

  getField(): string {
    return this.filter.extendedStringFilter.field;
  }

  getTitle(): string {
    return this.getFieldTitle(this.filter.extendedStringFilter.field);
  }

  getTooltip(): string {
    const rawFilter = this.filter;
    const json = JSON.stringify(rawFilter, omitTypename);
    const parsedFilter = JSON.parse(json);
    const operator = ExtendedStringFieldFilterHelper.getPathFilterInputOperator(parsedFilter as PersonFilterInput);
    const isNegate = this.isNegation();
    return i18next.t(`audience.filters.extendedStringFilterTooltip`, {
      type: i18next.t(`components.pathSelector.operators.${operator}${isNegate ? 'Negate' : ''}`),
      value: this.filter.extendedStringFilter.operator?.[operator],
      interpolation: { escapeValue: false },
    });
  }

  getFilterInput(): PersonFilterInput {
    return this.filter;
  }

  getCondition(): ExtendedStringOperatorKeys {
    if (!this.filter.extendedStringFilter.operator) return null;

    return EXTENDED_STRING_OPERATOR_KEYS.find(
      (key) => key === Object.keys(this.filter.extendedStringFilter.operator)[0],
    );
  }

  getValue(): ExtendedStringFilterValue {
    if (!this.filter.extendedStringFilter.operator) return null;
    return this.filter.extendedStringFilter.operator?.[this.getCondition()];
  }

  getValueText(): string {
    const value = this.getValue();
    if (typeof value === 'boolean') return value ? i18next.t('common.yes') : i18next.t('common.no');
    if (!value) return '';

    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  }

  shouldShowPopover(): boolean {
    return true;
  }

  isNegation(): boolean {
    return this.filter.extendedStringFilter.negate;
  }

  isCustom(): boolean {
    return false;
  }
}

class NumberFilterHelper extends PersonFilterHelper<NumberFilterValue, NumberOperatorKeys> {
  getType(): PersonFilterType {
    return PersonFilterType.Number;
  }

  getField(): string {
    return this.filter.numberFilter.customField || this.filter.numberFilter.field;
  }

  getTitle(): string {
    const field = this.getField();
    return this.filter.numberFilter.customField ? field : this.getFieldTitle(field);
  }

  getTooltip(): string {
    if (this.filter.numberFilter.operator?.between) {
      const from = this.filter.numberFilter.operator?.between?.from;
      const to = this.filter.numberFilter.operator?.between?.to;
      if (from && to) {
        return i18next.t('audience.filters.numberFilterBetweenTooltip', {
          name: this.getTitle(),
          from,
          to,
          interpolation: { escapeValue: false },
        });
      }
      if (from) {
        return i18next.t('audience.filters.numberFilterGreaterThanTooltip', {
          name: this.getTitle(),
          value: from,
          interpolation: { escapeValue: false },
        });
      }

      return i18next.t('audience.filters.numberFilterLowerThanTooltip', {
        name: this.getTitle(),
        value: to,
        interpolation: { escapeValue: false },
      });
    }

    if (this.filter.numberFilter.operator.eq) {
      return i18next.t('audience.filters.numberFilterEqualTooltip', {
        name: this.getTitle(),
        value: this.filter.numberFilter.operator.eq,
        interpolation: { escapeValue: false },
      });
    }

    if (this.filter.numberFilter.operator.gt) {
      return i18next.t('audience.filters.numberFilterGreaterThanTooltip', {
        name: this.getTitle(),
        value: this.filter.numberFilter.operator.gt,
        interpolation: { escapeValue: false },
      });
    }

    if (this.filter.numberFilter.operator.lt) {
      return i18next.t('audience.filters.numberFilterLowerThanTooltip', {
        name: this.getTitle(),
        value: this.filter.numberFilter.operator.lt,
        interpolation: { escapeValue: false },
      });
    }

    if (this.filter.numberFilter.operator.gte) {
      return i18next.t('audience.filters.numberFilterGreaterEqualTooltip', {
        name: this.getTitle(),
        value: this.filter.numberFilter.operator.gte,
        interpolation: { escapeValue: false },
      });
    }

    if (typeof this.filter.numberFilter.operator.exists === 'boolean') {
      if (this.filter.numberFilter.operator.exists) {
        return i18next.t('audience.filters.numberFilterExistsTooltip', {
          name: this.getTitle(),
          value: this.filter.numberFilter.operator.exists,
          interpolation: { escapeValue: false },
        });
      }
      return i18next.t('audience.filters.numberFilterNotExistsTooltip', {
        name: this.getTitle(),
        value: this.filter.numberFilter.operator.exists,
        interpolation: { escapeValue: false },
      });
    }

    return i18next.t('audience.filters.numberFilterLowerEqualTooltip', {
      name: this.getTitle(),
      value: this.filter.numberFilter.operator.lte,
      interpolation: { escapeValue: false },
    });
  }

  getFilterInput(): PersonFilterInput {
    // find key value pair in this.filter.
    const operator = Object.entries(this.filter.numberFilter.operator).find(
      ([key, value]) => NUMBER_OPERATOR_KEYS.includes(key as NumberOperatorKeys) && value !== null,
    )?.[0];

    const fieldConfig = this.filter.numberFilter.customField
      ? { customField: this.filter.numberFilter.customField }
      : { field: this.filter.numberFilter.field };

    if (operator === 'between') {
      return {
        numberFilter: {
          operator: {
            between: {
              from: this.filter.numberFilter.operator.between.from,
              to: this.filter.numberFilter.operator.between.to,
            },
          },
          ...fieldConfig,
        },
      };
    }
    return {
      numberFilter: {
        operator: {
          [operator]: this.filter.numberFilter.operator[operator],
        },
        ...fieldConfig,
      },
    };
  }

  getCondition(): NumberOperatorKeys {
    if (!this.filter.numberFilter.operator) return null;
    return NUMBER_OPERATOR_KEYS.find((key) => key === Object.keys(this.filter.numberFilter.operator)[0]);
  }

  getValue(): NumberFilterValue {
    if (!this.filter.numberFilter.operator) return null;
    return (this.filter.numberFilter.operator as NumberOperatorKeys)?.[this.getCondition()] as number | number[];
  }

  getValueText(): string {
    const value = this.getValue();
    if (value == null) return '';

    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return [value?.['from'], value?.['to']].filter(Boolean).join(', ');
    }
    return value.toString();
  }

  shouldShowPopover(): boolean {
    return this.getCondition() === NumberOperatorOperatorKey.between;
  }

  isNegation(): boolean {
    if (typeof this.filter.numberFilter?.operator?.exists === 'boolean')
      return !this.filter.numberFilter.operator.exists;
    return false;
  }

  isCustom(): boolean {
    return !!this.filter.numberFilter.customField;
  }
}

class BooleanFilterHelper extends PersonFilterHelper<BooleanFilterValue, BooleanOperatorKeys> {
  getType(): PersonFilterType {
    return PersonFilterType.Boolean;
  }
  getField(): string {
    return this.filter.booleanFilter.customField || this.filter.booleanFilter.field;
  }
  getTitle(): string {
    const field = this.getField();
    return this.filter.booleanFilter.customField ? field : this.getFieldTitle(field);
  }
  getTooltip(): string {
    if (typeof this.filter.booleanFilter.operator.exists === 'boolean') {
      if (this.filter.booleanFilter.operator.exists) {
        return i18next.t('audience.filters.booleanFilterExistsTooltip', {
          name: this.getTitle(),
          value: this.filter.booleanFilter.operator.exists,
        });
      }
      return i18next.t('audience.filters.booleanFilterNotExistsTooltip', {
        name: this.getTitle(),
        value: this.filter.booleanFilter.operator.exists,
      });
    }
    return i18next.t('audience.filters.booleanFilterTooltip', {
      name: this.getTitle(),
      value: this.filter.booleanFilter.operator.eq ? i18next.t('common.yes') : i18next.t('common.no'),
    });
  }
  getFilterInput(): PersonFilter {
    const fieldConfig = this.filter.booleanFilter.customField
      ? { customField: this.filter.booleanFilter.customField }
      : { field: this.filter.booleanFilter.field };

    if (typeof this.filter.booleanFilter.operator.exists === 'boolean') {
      return {
        booleanFilter: {
          operator: {
            exists: this.filter.booleanFilter.operator.exists,
          },
          ...fieldConfig,
        },
      };
    }
    return {
      booleanFilter: {
        operator: {
          eq: this.filter.booleanFilter.operator.eq,
        },
        ...fieldConfig,
      },
    };
  }

  getCondition(): BooleanOperatorKeys {
    if (!this.filter.booleanFilter.operator) return null;
    return BOOLEAN_OPERATOR_KEYS.find((key) => key === Object.keys(this.filter.booleanFilter.operator)[0]);
  }

  getValue(): BooleanFilterValue {
    if (!this.filter.booleanFilter.operator) return null;
    return this.filter.booleanFilter.operator.eq;
  }

  getValueText(): string {
    if (this.getValue() === undefined || this.getValue() === null) return '';

    return this.getValue() ? i18next.t('common.yes') : i18next.t('common.no');
  }

  isValidFilter(): boolean {
    return (
      this.getCondition() === 'exists' ||
      (!!this.getCondition() && this.getValue() !== undefined && this.getValue() !== null)
    );
  }

  shouldShowPopover(): boolean {
    return true;
  }

  isNegation(): boolean {
    if (typeof this.filter.booleanFilter?.operator?.exists === 'boolean')
      return !this.filter.booleanFilter.operator.exists;
    return false;
  }

  isCustom(): boolean {
    return !!this.filter.booleanFilter.customField;
  }
}

class GeoFilterHelper extends PersonFilterHelper<GeoFilterValue, null> {
  getType(): PersonFilterType {
    return PersonFilterType.Geo;
  }

  getField(): string {
    return 'geo';
  }

  getTitle(): string {
    return 'Geofilter';
  }

  getTooltip(): string {
    let filterInfo;
    if (this.filter.geoFilter.operator.in) {
      const field = this.filter.geoFilter.operator.in.field;
      const fieldLabel = field === 'Dma' ? i18next.t('geoFilters.listFieldDma') : i18next.t('geoFilters.listFieldZip');
      const condition = this.filter.geoFilter.negate
        ? i18next.t('geoFilters.listConditionNotIn')
        : i18next.t('geoFilters.listConditionIn');
      const values = this.filter.geoFilter.operator.in.values || [];
      const preview = values.slice(0, 20).join(', ');
      const suffix = values.length > 20 ? `, ... (+${values.length - 20} more)` : '';
      filterInfo = `${fieldLabel} ${condition}: ${preview}${suffix}`;
    } else if (this.filter.geoFilter.operator.polygon) {
      const polygonKey = this.filter.geoFilter.negate
        ? 'audience.filters.geoFilterPolygonTooltipNegate'
        : 'audience.filters.geoFilterPolygonTooltip';
      filterInfo = i18next.t(polygonKey);
    } else {
      const address = [this.filter.geoFilter.place?.address, this.filter.geoFilter.place?.postalCode]
        .map((component) => component?.trim())
        .filter((component) => !!component)
        .join(', ');

      const radiusKey = this.filter.geoFilter.negate
        ? 'audience.filters.geoFilterTooltipNegate'
        : 'audience.filters.geoFilterTooltip';
      filterInfo = i18next.t(radiusKey, {
        radius: this.filter.geoFilter.operator.distance?.distance,
        units: this.filter.geoFilter.operator.distance?.unit?.toLowerCase(),
        address,
        interpolation: { escapeValue: false },
      });
    }
    if (this.filter.geoFilter.excludedZipCodes?.length) {
      filterInfo = filterInfo.concat(
        `\n${i18next.t('newGlobalSearchAudience.excludedZips')}: ${this.filter.geoFilter.excludedZipCodes?.join(', ')}`,
      );
    }
    return filterInfo;
  }

  getFilterInput(): PersonFilterInput {
    const jsonPolygon =
      this.filter.geoFilter.operator.polygon && JSON.stringify(this.filter.geoFilter.operator.polygon, omitTypename);
    return {
      geoFilter: {
        operator: {
          ...(this.filter.geoFilter.operator.distance && {
            distance: {
              distance: this.filter.geoFilter.operator.distance.distance,
              location: {
                longitude: this.filter.geoFilter.operator.distance.location.longitude,
                latitude: this.filter.geoFilter.operator.distance.location.latitude,
              },
              unit: this.filter.geoFilter.operator.distance.unit,
            },
          }),
          ...(jsonPolygon && { polygon: JSON.parse(jsonPolygon) }),
          ...(this.filter.geoFilter.operator.in && {
            in: {
              field: this.filter.geoFilter.operator.in.field,
              values: this.filter.geoFilter.operator.in.values,
            },
          }),
        },
        ...(this.filter.geoFilter.negate != null && { negate: this.filter.geoFilter.negate }),
        ...(this.filter.geoFilter.place && {
          place: {
            address: this.filter.geoFilter.place.address,
            postalCode: this.filter.geoFilter.place.postalCode,
          },
        }),
        ...(this.filter.geoFilter.excludedZipCodes && {
          excludedZipCodes: this.filter.geoFilter.excludedZipCodes,
        }),
      },
    };
  }

  getValue(): GeoFilterValue {
    return null;
  }

  getCondition(): null {
    return null;
  }

  isValidFilter(): boolean {
    return true;
  }

  getValueText(): string {
    return '';
  }

  shouldShowPopover(): boolean {
    return false;
  }

  isCustom(): boolean {
    return false;
  }
}

class DateFilterHelper extends PersonFilterHelper<DateFilterValue, DateOperatorKeys> {
  getType(): PersonFilterType {
    return PersonFilterType.Date;
  }
  getField(): string {
    return this.filter.dateFilter.customField || this.filter.dateFilter.field;
  }
  getTitle(): string {
    const field = this.getField();
    return this.filter.dateFilter.customField ? field : this.getFieldTitle(field);
  }
  formatDate = (date: string) => moment.utc(date).format('YYYY/MM/DD');

  getTooltip(): string {
    if (this.filter.dateFilter.operator?.between) {
      const from = this.filter.dateFilter.operator?.between?.from;
      const to = this.filter.dateFilter.operator?.between?.to;
      if (from && to) {
        return i18next.t('audience.filters.dateFilterBetweenTooltip', {
          name: this.getTitle(),
          from: this.formatDate(from as string),
          to: this.formatDate(to as string),
          interpolation: { escapeValue: false },
        });
      }
      if (from) {
        return i18next.t('audience.filters.dateFilterGreaterThanTooltip', {
          name: this.getTitle(),
          value: this.formatDate(from as string),
          interpolation: { escapeValue: false },
        });
      }

      return i18next.t('audience.filters.dateFilterLowerThanTooltip', {
        name: this.getTitle(),
        value: this.formatDate(to as string),
        interpolation: { escapeValue: false },
      });
    }

    if (this.filter.dateFilter.operator.gt) {
      return i18next.t('audience.filters.dateFilterGreaterThanTooltip', {
        name: this.getTitle(),
        value: this.formatDate(this.filter.dateFilter.operator.gt as string),
        interpolation: { escapeValue: false },
      });
    }

    if (this.filter.dateFilter.operator.lt) {
      return i18next.t('audience.filters.dateFilterLowerThanTooltip', {
        name: this.getTitle(),
        value: this.formatDate(this.filter.dateFilter.operator.lt as string),
        interpolation: { escapeValue: false },
      });
    }

    if (this.filter.dateFilter.operator.gte) {
      return i18next.t('audience.filters.dateFilterGreaterEqualTooltip', {
        name: this.getTitle(),
        value: this.formatDate(this.filter.dateFilter.operator.gte as string),
        interpolation: { escapeValue: false },
      });
    }

    if (typeof this.filter.dateFilter.operator.exists === 'boolean') {
      if (this.filter.dateFilter.operator.exists) {
        return i18next.t('audience.filters.dateFilterExistsTooltip', {
          name: this.getTitle(),
          value: this.filter.dateFilter.operator.exists,
          interpolation: { escapeValue: false },
        });
      }
      return i18next.t('audience.filters.dateFilterNotExistsTooltip', {
        name: this.getTitle(),
        value: this.filter.dateFilter.operator.exists,
        interpolation: { escapeValue: false },
      });
    }

    return i18next.t('audience.filters.dateFilterLowerEqualTooltip', {
      name: this.getTitle(),
      value: this.formatDate(this.filter.dateFilter.operator.lte as string),
      interpolation: { escapeValue: false },
    });
  }

  getFilterInput(): PersonFilterInput {
    const fieldConfig = this.filter.dateFilter.customField
      ? { customField: this.filter.dateFilter.customField }
      : { field: this.filter.dateFilter.field };

    return {
      dateFilter: {
        operator: this.filter.dateFilter.operator,
        ...fieldConfig,
      },
    };
  }

  getCondition(): DateOperatorKeys {
    if (!this.filter.dateFilter.operator) return null;
    return Object.keys(this.filter.dateFilter.operator)[0] as DateOperatorKeys;
  }

  getValue(): string {
    if (this.filter.dateFilter.operator.between) {
      return this.filter.dateFilter.operator.between.from
        ? this.formatDate(this.filter.dateFilter.operator.between.from as string)
        : null;
    }
    return this.filter.dateFilter.operator[this.getCondition()]
      ? this.formatDate(this.filter.dateFilter.operator[this.getCondition()] as string)
      : null;
  }
  getValueText(): string {
    if (this.filter.dateFilter.operator.between) {
      const fromDate = this.formatDate(this.filter.dateFilter.operator.between.from as string);
      const toDate = this.formatDate(this.filter.dateFilter.operator.between.to as string);
      return this.filter.dateFilter.operator.between.from ? `${fromDate} - ${toDate}` : null;
    }
    return this.formatDate(this.filter.dateFilter.operator[this.getCondition()] as string);
  }

  shouldShowPopover(): boolean {
    return false;
  }

  shouldShowCustomInput(): boolean {
    return true;
  }

  isCustom(): boolean {
    return !!this.filter.dateFilter.customField;
  }
}

export default PersonFilterHelper;
